// Ufer im Run (Erste Fassung, Runde 1): Boden, Licht und Laub je Biom (R41), der Durchlass am Rand (R42), das Gift, das
// überspringt, und das Kräftemessen (K53, K54). Reine Darstellung; public/art/ gehört dem Grafik-Lead, hier wird nur
// gelesen. Fehlt ein Bild, zeichnet der Code einen schlichten Platzhalter, und nichts bricht.
import { combatRules, getBiome } from '../../content/index.ts';
import type { BattleResult } from '../../sim/index.ts';
import { blit, drawStrip, fxFrame, fxSeconds, image, pixelScale, sprite, sprites, type ArtSprite } from '../art.ts';
import type { DrawUnit } from '../render.ts';
import { drawLeaves, drawMarker, drawPracticeLight } from '../practice/look.ts';
import { figureGeo, pointOf } from '../figures.ts';

const FIELD = combatRules.field;

/** Der Boden des Übungsufers, eingefärbt nach dem Biom, einmal vorbereitet je Biom und Fassung (trocken, nass) */
const tinted = new Map<string, HTMLCanvasElement>();
function tintedGround(id: string, wet: boolean): CanvasImageSource | null {
  const own = sprite('ui', `boden-${id}${wet ? '-nass' : ''}`);
  const ownImg = own ? image(own.file) : null;
  if (ownImg) return ownImg;
  const base = sprite('ui', `boden-uebungsufer${wet ? '-nass' : ''}`);
  const img = base ? image(base.file) : null;
  if (!img) return null;
  const b = getBiome(id);
  if (!b.ground.tint || b.ground.alpha <= 0) return img;
  const key = `${id}:${wet}`;
  let c = tinted.get(key);
  if (!c) {
    c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const x = c.getContext('2d')!;
    x.drawImage(img, 0, 0);
    // Färben wie bei einem Farbfilter: erst multiplizieren, dann etwas der Leitfarbe darüber
    x.globalCompositeOperation = 'multiply';
    x.globalAlpha = b.ground.alpha * 1.6;
    x.fillStyle = b.ground.tint;
    x.fillRect(0, 0, c.width, c.height);
    x.globalCompositeOperation = 'soft-light';
    x.globalAlpha = b.ground.alpha;
    x.fillRect(0, 0, c.width, c.height);
    tinted.set(key, c);
  }
  return c;
}

function fieldImage(ctx: CanvasRenderingContext2D, s: number, img: CanvasImageSource): void {
  ctx.imageSmoothingEnabled = false;
  const w = img instanceof HTMLImageElement ? img.naturalWidth : (img as HTMLCanvasElement).width;
  const h = img instanceof HTMLImageElement ? img.naturalHeight : (img as HTMLCanvasElement).height;
  ctx.drawImage(img, 0, 0, w, h, 0, 0, FIELD.width * s, FIELD.height * s);
}

/**
 * R41: der Boden des Ufers im Biom (ui boden-<biom>, sonst der Boden des Übungsufers in der Leitfarbe des Bioms); im
 * Starkregen blendet die nasse Fassung darüber. false ohne Bild.
 */
export function drawBiomeGround(ctx: CanvasRenderingContext2D, s: number, biome: string, rain: number): boolean {
  const dry = tintedGround(biome, false);
  if (!dry) return false;
  fieldImage(ctx, s, dry);
  const wet = rain > 1 ? tintedGround(biome, true) : null;
  if (wet) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, (rain - 1) * 1.5);
    fieldImage(ctx, s, wet);
    ctx.restore();
  }
  return true;
}

/**
 * Licht und, wo es passt, fallendes Laub über dem Feld. Runde 8 (Schlussrunde des Grafik-Leads): das Licht des Bioms
 * (ui licht-<biom>), am Durchlass das Abendlicht (licht-<biom>-abend), sonst wie bisher das Licht des Übungsufers; im
 * Starkregen gedämpft (alphaNass) und schwächer, je näher das Unwetter ist (stormTint aus R25)
 */
export function drawBiomeAir(ctx: CanvasRenderingContext2D, s: number, biome: string, T: number, evening = false, rain = 1, stormTint = 0): void {
  if (getBiome(biome).leaves) drawLeaves(ctx, s, T);
  const wet = rain > 1 ? Math.min(1, (rain - 1) * 1.5) : 0;
  const weaker = Math.min(1, 2 * stormTint);
  for (const id of evening ? [`licht-${biome}-abend`, `licht-${biome}`] : [`licht-${biome}`]) if (drawPracticeLight(ctx, s, id, wet, weaker)) return;
  drawPracticeLight(ctx, s, 'licht-uebungsufer', wet, weaker);
}

// ---------- Der Durchlass (R42) ----------

/**
 * R42 (Fabi, 29.10.2026): Der Durchlass braucht keine Erklärung, sieht aber in jedem Biom anders aus. Er steht am rechten
 * Rand hinter den Sturmwesen, mit dem Bild des Grafik-Leads (marker durchlass-<biom>), sonst als Platzhalter: im
 * Tannenwald zwei umgestürzte Tannen, im Moor ein Bogen aus Wurzeln mit Irrlichtern, an den Weinhängen ein Tor in der
 * Trockenmauer.
 */
export function drawGate(ctx: CanvasRenderingContext2D, s: number, biome: string, T: number, rain = 1): void {
  const x = FIELD.width - 6;
  const y = FIELD.height / 2;
  if (drawMarker(ctx, s, `durchlass-${biome}`, x, y + 22, T)) {
    // Runde 7: Im Starkregen blendet die nasse Fassung darüber wie beim Boden
    if (rain > 1) drawMarker(ctx, s, `durchlass-${biome}-nass`, x, y + 22, T, { alpha: Math.min(1, (rain - 1) * 1.5) });
    return;
  }
  ctx.save();
  if (biome === 'moor') {
    // Bogen aus Wurzeln, Schilf davor, drei Irrlichter
    ctx.lineCap = 'round';
    for (const [w, col] of [[3.2, '#3d2f25'], [1.6, '#5c4a3a']] as const) {
      ctx.strokeStyle = col;
      ctx.lineWidth = w * s;
      ctx.beginPath();
      ctx.moveTo((x - 5) * s, (y + 24) * s);
      ctx.bezierCurveTo((x - 14) * s, (y - 2) * s, (x - 10) * s, (y - 30) * s, (x + 2) * s, (y - 26) * s);
      ctx.moveTo((x - 2) * s, (y + 24) * s);
      ctx.bezierCurveTo((x + 6) * s, (y + 4) * s, (x + 8) * s, (y - 18) * s, (x + 2) * s, (y - 26) * s);
      ctx.stroke();
    }
    ctx.strokeStyle = '#6f7a3f';
    ctx.lineWidth = Math.max(1, 0.5 * s);
    for (let i = 0; i < 14; i++) {
      const rx = x - 14 + i * 1.6 + Math.sin(i * 2.3) * 0.8;
      const sway = Math.sin(T * 1.3 + i) * 0.6;
      ctx.beginPath();
      ctx.moveTo(rx * s, (y + 26) * s);
      ctx.quadraticCurveTo((rx + sway) * s, (y + 20) * s, (rx + sway * 2) * s, (y + 14 + (i % 3) * 2) * s);
      ctx.stroke();
    }
    for (let i = 0; i < 3; i++) {
      const lx = x - 8 + i * 6 + Math.sin(T * 0.7 + i * 2) * 2;
      const ly = y - 8 - i * 7 + Math.cos(T * 0.9 + i) * 2;
      const g = ctx.createRadialGradient(lx * s, ly * s, 0, lx * s, ly * s, 4 * s);
      g.addColorStop(0, 'rgba(190, 240, 210, 0.85)');
      g.addColorStop(1, 'rgba(120, 200, 170, 0)');
      ctx.fillStyle = g;
      ctx.fillRect((lx - 4) * s, (ly - 4) * s, 8 * s, 8 * s);
    }
  } else if (biome === 'weinhaenge') {
    // Trockenmauer mit einem Tor: Steine oben und unten, in der Mitte frei
    ctx.fillStyle = '#9a8566';
    ctx.strokeStyle = '#6e5d45';
    ctx.lineWidth = Math.max(1, 0.35 * s);
    for (let row = 0; row < 26; row++) {
      const yy = row * 4 - 2;
      if (yy > y - 14 && yy < y + 14) continue;
      for (let c = 0; c < 2; c++) {
        const xx = x - 6 + c * 5 + (row % 2) * 2.5;
        ctx.beginPath();
        ctx.roundRect(xx * s, yy * s, 4.6 * s, 3.6 * s, 0.8 * s);
        ctx.fill();
        ctx.stroke();
      }
    }
    // Torpfosten: zwei große Steine an den Enden der Lücke
    ctx.fillStyle = '#b39a72';
    for (const yy of [y - 15, y + 11]) {
      ctx.beginPath();
      ctx.roundRect((x - 7) * s, yy * s, 11 * s, 4.4 * s, 1.2 * s);
      ctx.fill();
      ctx.stroke();
    }
    // eine Rebe mit Trauben
    ctx.fillStyle = '#6a3f6e';
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.arc((x - 9 + (i % 2)) * s, (y - 16 + i * 1.3) * s, 0.9 * s, 0, Math.PI * 2);
      ctx.fill();
    }
  } else {
    // Tannenwald: zwei umgestürzte Tannen, schräg gegeneinander gelehnt
    const trunk = (x0: number, y0: number, x1: number, y1: number) => {
      for (const [w, col] of [[4, '#3b2a1c'], [2.6, '#6b4a30']] as const) {
        ctx.strokeStyle = col;
        ctx.lineWidth = w * s;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(x0 * s, y0 * s);
        ctx.lineTo(x1 * s, y1 * s);
        ctx.stroke();
      }
      // ein paar dunkle Zweige
      ctx.strokeStyle = '#2f4a33';
      ctx.lineWidth = 1.2 * s;
      for (let i = 1; i < 5; i++) {
        const k = i / 5;
        const px = x0 + (x1 - x0) * k;
        const py = y0 + (y1 - y0) * k;
        ctx.beginPath();
        ctx.moveTo(px * s, py * s);
        ctx.lineTo((px + 3) * s, (py - 2.5) * s);
        ctx.stroke();
      }
    };
    trunk(x - 13, y + 30, x - 2, y - 20);
    trunk(x + 7, y + 30, x - 4, y - 21);
  }
  ctx.restore();
}

// ---------- Gift springt über (K53) ----------

/** So lange fliegt der Tropfen vom Vertriebenen zum Nächsten (s) */
const JUMP_SECONDS = 0.55;

/**
 * K53 (Fabi, 29.10.2026: Gift, das auf den Nächsten überspringt, soll man deutlich sehen; sein Lieblingsmoment neben dem
 * Eisvogel): Fällt ein vergifteter Gegner, fliegt ein Tropfen im Bogen zum Nächsten (Runde 7: gift-sprung, ein Bild je
 * Neigung), beim Absprung und bei der Ankunft spritzt es kurz grün (gift-ueberspringen), und groß steht die Zahl der Stapel
 * darüber
 */
export function drawPoisonJumps(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, units: DrawUnit[], T: number, frameAt: (uid: number, t: number) => { x: number; y: number } | null): void {
  const byUid = new Map(units.map((u) => [u.uid, u]));
  const k = pixelScale(s);
  const splash = sprite('effect', 'gift-ueberspringen');
  const splashFor = splash ? fxSeconds(splash) : 0;
  for (const e of r.events) {
    if (e.t > T) break;
    if (e.type !== 'poisonJump') continue;
    const age = T - e.t;
    if (age > JUMP_SECONDS + 1.2) continue;
    const from = frameAt(e.from, e.t);
    const toU = byUid.get(e.to);
    const to = toU ? { x: toU.x, y: toU.y } : frameAt(e.to, T);
    if (!from || !to) continue;
    const q = Math.min(1, age / JUMP_SECONDS);
    const lift = 14 + Math.hypot(to.x - from.x, to.y - from.y) * 0.25;
    const yAt = (kk: number) => from.y - 4 + (to.y - from.y) * kk - Math.sin(Math.PI * kk) * lift;
    // Absprung und Ankunft: der kleine grüne Spritzer an der Körpermitte
    if (splash && age < splashFor) drawStrip(ctx, 'effect', 'gift-ueberspringen', fxFrame(splash, age) ?? 0, from.x * s, (from.y - 4) * s, k);
    if (splash && q >= 1 && age - JUMP_SECONDS < splashFor) drawStrip(ctx, 'effect', 'gift-ueberspringen', fxFrame(splash, age - JUMP_SECONDS) ?? 0, to.x * s, (to.y - 4) * s, k);
    if (q < 1) {
      const px = from.x + (to.x - from.x) * q;
      const vy = yAt(Math.min(1, q + 0.02)) - yAt(Math.max(0, q - 0.02));
      const tilt = vy < -0.6 ? 1 : vy > 0.6 ? 2 : 0;
      if (!drawStrip(ctx, 'projectile', 'gift-sprung', tilt, px * s, yAt(q) * s, k, { flip: to.x < from.x })) drawStrip(ctx, 'projectile', 'gifttropfen', 0, px * s, yAt(q) * s, k);
    } else {
      // Ankunft: die Zahl der Stapel, kurz über dem Kopf
      const a = Math.max(0, 1 - (age - JUMP_SECONDS) / 1.2);
      const grow = (age - JUMP_SECONDS) / 1.2;
      ctx.save();
      ctx.font = `bold ${Math.round(Math.max(13, s * 3.4))}px Zweigbuch, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.lineWidth = Math.max(2, s * 0.6);
      ctx.strokeStyle = `rgba(20, 30, 15, ${0.8 * a})`;
      ctx.fillStyle = `rgba(160, 235, 110, ${a})`;
      const label = `Gift springt über! ${e.stacks}`;
      const ly = (to.y - 14 - grow * 4) * s;
      ctx.strokeText(label, to.x * s, ly);
      ctx.fillText(label, to.x * s, ly);
      ctx.restore();
    }
  }
}

// ---------- Kräftemessen (K54) ----------

/** K54: Seit wann läuft das Kräftemessen, und ab wann wird es härter? null, wenn (noch) nicht */
export function duelAt(r: BattleResult, T: number): { since: number; at: number } | null {
  for (const e of r.events) {
    if (e.t > T) break;
    if (e.type === 'duel') return { since: e.t, at: e.at };
  }
  return null;
}

/** K54: Beim Kräftemessen rückt ein dunkler Rand ins Feld, der langsam pulsiert; man sieht, dass es jetzt ernst wird */
export function drawDuel(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, T: number): void {
  const d = duelAt(r, T);
  if (!d || T >= r.duration + 0.5) return;
  const w = FIELD.width * s;
  const h = FIELD.height * s;
  const ramp = Math.min(1, (T - d.since) / 1.5);
  const hard = T >= d.at ? 1 : 0.5;
  const pulse = 0.5 + 0.5 * Math.sin(T * 3);
  const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.62);
  g.addColorStop(0, 'rgba(60, 10, 20, 0)');
  g.addColorStop(1, `rgba(70, 14, 24, ${(0.22 + 0.12 * pulse) * ramp * hard})`);
  ctx.save();
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // Beim Beginn steht es kurz groß oben im Feld
  const age = T - d.since;
  if (age < 2.4) {
    const a = Math.min(1, age / 0.3) * Math.min(1, (2.4 - age) / 0.6);
    ctx.textAlign = 'center';
    ctx.font = `700 ${Math.round(Math.max(20, s * 5.5))}px Zweigbuch, Georgia, serif`;
    ctx.lineWidth = Math.max(3, s * 0.9);
    ctx.strokeStyle = `rgba(30, 12, 16, ${0.85 * a})`;
    ctx.fillStyle = `rgba(255, 214, 170, ${a})`;
    ctx.strokeText('Kräftemessen!', w / 2, h * 0.2);
    ctx.fillText('Kräftemessen!', w / 2, h * 0.2);
    ctx.font = `${Math.round(Math.max(12, s * 2.4))}px Zweigbuch, system-ui, sans-serif`;
    ctx.lineWidth = Math.max(2, s * 0.5);
    ctx.strokeText('Jetzt treffen alle immer härter', w / 2, h * 0.2 + s * 4.5);
    ctx.fillText('Jetzt treffen alle immer härter', w / 2, h * 0.2 + s * 4.5);
  }
  ctx.restore();
}

// ---------- Band aus dem Gemeinschaftsraum (B25) ----------

type Spot = { x: number; y: number; radius: number };

/**
 * B25 (Fabi, 29.10.2026): Wer eine Schicht zusammen im Gemeinschaftsraum war, kämpft nebeneinander besser. Stehen die
 * beiden nah genug, verbindet sie ein warmes Band mit einem Herz; stehen sie zu weit auseinander, zeigt beim Aufstellen
 * eine gepunktete Linie, dass sie zusammengehören
 */
export function drawBond(ctx: CanvasRenderingContext2D, s: number, a: Spot, b: Spot, reach: number, setup: boolean, T: number): void {
  const gap = Math.hypot(a.x - b.x, a.y - b.y) - a.radius - b.radius;
  const close = gap <= reach;
  if (!close && !setup) return;
  ctx.save();
  ctx.strokeStyle = close ? 'rgba(255, 150, 170, 0.75)' : 'rgba(255, 150, 170, 0.35)';
  ctx.lineWidth = Math.max(1.5, s * (close ? 0.45 : 0.3));
  if (!close) ctx.setLineDash([s * 0.8, s * 1.2]);
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - 3;
  ctx.beginPath();
  ctx.moveTo(a.x * s, a.y * s);
  ctx.quadraticCurveTo(mx * s, (my - 2) * s, b.x * s, b.y * s);
  ctx.stroke();
  ctx.setLineDash([]);
  if (close) {
    // ein kleines Herz in der Mitte, das sacht pocht
    const k = s * (0.9 + 0.12 * Math.sin(T * 4));
    const hx = mx * s;
    const hy = (my - 1) * s;
    ctx.fillStyle = '#ff8fa8';
    ctx.strokeStyle = 'rgba(80, 20, 35, 0.8)';
    ctx.lineWidth = Math.max(1, s * 0.2);
    ctx.beginPath();
    ctx.moveTo(hx, hy + 1.2 * k);
    ctx.bezierCurveTo(hx - 2.2 * k, hy - 0.4 * k, hx - 1.2 * k, hy - 2.2 * k, hx, hy - 1 * k);
    ctx.bezierCurveTo(hx + 1.2 * k, hy - 2.2 * k, hx + 2.2 * k, hy - 0.4 * k, hx, hy + 1.2 * k);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * S13 Otter, Pfote halten (Runde 7, Bild des Grafik-Leads): Solange Otter und Freund nah genug beieinander stehen, einmal
 * in der Mitte zwischen beiden zwei Pfotenabdrücke und ein pochendes Herz, etwas über ihren Köpfen. false ohne Bild
 */
export function drawPaw(ctx: CanvasRenderingContext2D, s: number, a: DrawUnit, b: DrawUnit, reach: number, T: number): boolean {
  const sp = sprite('status', 'pfote-halten');
  if (!sp || !image(sp.file)) return false;
  if (Math.hypot(a.x - b.x, a.y - b.y) - a.radius - b.radius > reach) return true;
  const head = (u: DrawUnit) => {
    const g = figureGeo(u, s);
    return g ? (pointOf(g, 'kopf')?.y ?? g.top) : (u.y - u.radius * 2.5) * s;
  };
  const k = pixelScale(s);
  const y = Math.min(head(a), head(b)) - 4 * k;
  drawStrip(ctx, 'status', 'pfote-halten', fxFrame(sp, T + a.uid) ?? 0, ((a.x + b.x) / 2) * s, y, k);
  return true;
}

/** S13 Baum fällen (Runde 7): das Bild für die Richtung des Streifens; gezeichnet für Blick nach rechts, sonst gespiegelt */
export function fellArt(kind: 'effect' | 'marker', prefix: string, dx: number, dy: number): { sp: ArtSprite; flip: boolean } | null {
  const want = (Math.atan2(dy, Math.abs(dx)) * 180) / Math.PI;
  const list = sprites().filter((sp) => sp.kind === kind && (sp.id === prefix || sp.id.startsWith(`${prefix}-hoch`) || sp.id.startsWith(`${prefix}-runter`)) && sp.angle !== undefined);
  if (!list.length) return null;
  const sp = list.reduce((a, b) => (Math.abs(b.angle! - want) < Math.abs(a.angle! - want) ? b : a));
  return { sp, flip: dx < 0 };
}

/** Ein Bild eines Streifens mit seinem center auf (x, y) Feldeinheiten, gespiegelt um center */
function fellFrame(ctx: CanvasRenderingContext2D, s: number, sp: ArtSprite, frame: number, x: number, y: number, flip: boolean, alpha = 1): void {
  const img = image(sp.file);
  if (!img) return;
  const [fw, fh] = sp.frameSize ?? [img.naturalWidth, img.naturalHeight];
  const c = sp.center ?? [0, fh - 1];
  ctx.save();
  ctx.globalAlpha *= alpha;
  blit(ctx, img, frame * fw, 0, fw, fh, x * s, y * s, [c[0], c[1] - 0.5], pixelScale(s), flip);
  ctx.restore();
}

/**
 * S13 Biber, Baum fällen (Runde 7, Bilder des Grafik-Leads): Solange die Ansage läuft, steht der Baum vor dem nagenden
 * Biber (stand, vor den Figuren gezeichnet, also hinter ihnen); kurz vor dem Treffer bricht er, fällt, kracht in den
 * Streifen und liegt (fall, über allen Figuren), dann verblasst er
 */
export function drawFelledTrees(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, T: number, phase: 'stand' | 'fall'): void {
  for (const e of r.events) {
    if (e.t > T) break;
    if (e.type !== 'telegraph' || e.kniff !== 'baum-faellen' || e.shape.kind !== 'stripe') continue;
    const sh = e.shape;
    const art = fellArt('effect', 'baum-faellt', sh.dx, sh.dy);
    if (!art) continue;
    const lead = (art.sp.leadMs ?? 180) / 1000;
    const done = r.events.find((x) => x.type === 'telegraphEnd' && x.id === e.id);
    // fällt die Ansage aus (der Biber fällt vorher), bleibt der Baum stehen und verschwindet mit ihr
    if (done && done.type === 'telegraphEnd' && !done.done) {
      if (phase === 'stand' && T < done.t) fellFrame(ctx, s, art.sp, 0, sh.x, sh.y, art.flip);
      continue;
    }
    const fallAt = e.at - lead;
    if (T < fallAt) {
      if (phase === 'stand') fellFrame(ctx, s, art.sp, 0, sh.x, sh.y, art.flip);
      continue;
    }
    if (phase !== 'fall') continue;
    const age = T - fallAt;
    const total = fxSeconds(art.sp);
    const frame = fxFrame({ ...art.sp, hold: true }, age);
    const fade = Math.max(0, 1 - Math.max(0, age - total) / 0.6);
    if (frame !== null && fade > 0) fellFrame(ctx, s, art.sp, frame, sh.x, sh.y, art.flip, fade);
  }
}
